/**
 * Thanksgiving cross-stitch patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingCrossStitchPatternsPack: ToilePack = {
    slug: 'thanksgiving-cross-stitch-patterns',
    category: 'season',
    name: 'Thanksgiving cross-stitch patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'turkey', label: 'Turkey chart', subject: 'A turkey'},
        {key: 'pumpkin', label: 'Pumpkin chart', subject: 'A pumpkin'},
        {key: 'acorn', label: 'Acorn chart', subject: 'An acorn'},
    ],
    styles: [
        {key: 'chunky', label: 'Chunky chart', style: 'a chunky low-count cross-stitch chart, large bold stitch symbols on a coarse grid', palette: 'Black symbols on white grid', framing: 'Centered on the page with a wide white margin, a symbol key in the corner'},
        {key: 'fine', label: 'Fine chart', style: 'a fine high-count cross-stitch chart, small detailed stitch symbols on a dense grid', palette: 'Black symbols on white grid', framing: 'Filling the page within a moderate white margin, a symbol key in the corner'},
        {key: 'mini', label: 'Mini chart', style: 'a small mini quick-stitch chart, a compact symbol grid', palette: 'Black symbols on white grid', framing: 'Centered on the page with a wide white margin, a symbol key in the corner'},
    ],
    directions: ['a bookmark-sized chart', 'as a repeating border', 'a maple leaf instead'],
    directionHint: 'try: a bookmark-sized chart',
    seo: {
        title: 'Thanksgiving cross stitch: turkey, pumpkin, acorn',
        metaDescription: 'Nine Thanksgiving cross-stitch charts — turkey, pumpkin, acorn — in chunky, fine or mini stitch-count grids, gridded and keyed.',
        h1: 'Thanksgiving cross stitch patterns: turkey, pumpkin, acorn',
        tagline: 'Nine charts, three autumn motifs, three stitch-count grids each — ruled square by square with a key.',
        intro: "A cross-stitch chart for the season: a turkey, a pumpkin, an acorn, each charted three ways — a chunky low-count grid with bold symbols, a fine high-count grid with detailed symbols, and a compact mini quick-stitch grid. Every chart carries a symbol key in the corner. Print it and tape it to the frame, or ask for a different motif and it's charted again.",
        useCases: [
            'a chart taped to the embroidery frame',
            'a small Thanksgiving gift for a stitcher',
            'a first project for a new stitcher',
            'a seasonal chart swapped by direction',
        ],
        faq: [
            {q: 'What count is the chart sized for?', a: 'The fine chart is drawn for a dense high-count grid, the chunky chart for a coarse low-count grid — pick whichever matches your fabric and patience.'},
            {q: 'Do I need the chunky or the fine chart?', a: 'Either works alone; the mini chart is there for a quick gift-sized stitch when a full chart is more than you need.'},
            {q: 'Can I get a different autumn motif?', a: "Yes — use the direction field to ask for a different motif, like a bookmark-sized chart or a maple leaf, and it's charted again in all three grid sizes."},
        ],
    },
}
